import {
    ZfCard,
    ZfCardMedia,
    ZfCardMediaHeader,
    ZfDialog,
    ZfDialogContent,
    ZfDialogFooter,
    ZfDialogHeader,
    ZfStack,
    ZfTextButton,
    ZfTypography,
} from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getImageUrlByString } from '../../../../styles/assets/imageMap';
import { useContentByTopicIdAndDomicile } from '../../../api';
import type { TalkingPointDto } from '../../../api/generated/client';
import type { IThinTopic } from '../../../interfaces/topic';
import { List } from '../../atoms';

interface IProps {
    isOpen: boolean;
    topics: IThinTopic[];
    selectedTopic?: IThinTopic;
    onTopicSelected: (topic?: IThinTopic) => void;
    onTalkingPointSelected: (event: TalkingPointDto) => void;
    onClose: () => void;
}

export const SelectTalkingPointModal: FunctionComponent<IProps> = ({
    isOpen,
    topics,
    selectedTopic: initialTopic,
    onTopicSelected,
    onTalkingPointSelected,
    onClose,
}) => {
    const { t, i18n } = useTranslation();
    const [selectedTopic, setSelectedTopic] = useState<IThinTopic | undefined>(initialTopic);
    const [selectedTalkingPoint, setSelectedTalkingPoint] = useState<TalkingPointDto | undefined>(undefined);
    const [selectedTalkingPointIndex, setSelectedTalkingPointIndex] = useState<number | undefined>(-1);

    const { data: talkingPoints, refetch, isRefetching } = useContentByTopicIdAndDomicile(selectedTopic?.id);

    const [talkingPointsForSelectedTopic, setTalkingPointsForSelectedTopic] = useState(talkingPoints);

    useEffect(() => {
        setTalkingPointsForSelectedTopic(talkingPoints);
    }, [talkingPoints, selectedTopic]);

    const onTopicClick = (topic: IThinTopic, selected: boolean) => {
        refetch();
        const topic_ = selected ? topic : undefined;
        setSelectedTopic(topic_);
        setSelectedTalkingPointIndex(-1);
        setSelectedTalkingPoint(undefined);
        onTopicSelected(topic_);
    };

    const onTalkingPointClick = (talkingPoint: TalkingPointDto, index: number) => {
        if (selectedTalkingPointIndex !== -1 && selectedTalkingPointIndex === index) {
            setSelectedTalkingPointIndex(undefined);
            setSelectedTalkingPoint(undefined);
        } else {
            setSelectedTalkingPointIndex(index);
            setSelectedTalkingPoint(talkingPoint);
        }
    };

    const onDialogConfirm = () => onTalkingPointSelected(selectedTalkingPoint!);

    return (
        <ZfDialog show={isOpen} onDialogCancel={onClose} onDialogClose={onClose} size="m" className="select-topic-modal">
            <ZfDialogHeader>{t('zfEventModalNewNeed')}</ZfDialogHeader>
            <ZfDialogContent>
                <ZfStack direction="row" spacing="extra-large">
                    <ZfStack direction="column" spacing="extra-small" style={{ maxHeight: '60vh', overflowY: 'scroll', overflowX: 'hidden' }}>
                        {topics.map((topic) => (
                            <ZfCard
                                key={topic.id}
                                className="action-dialog-selectable-card"
                                onCardSelect={(event: CustomEvent<boolean>) => onTopicClick(topic, event.detail)}
                                selectable={true}
                                selected={selectedTopic?.id === topic.id}
                            >
                                <ZfCardMedia image={getImageUrlByString(topic.key)} style={{ height: '140px' }}>
                                    <ZfCardMediaHeader>
                                        <ZfTypography slot="leading" variant="body-large" color="invert">
                                            {topic.name[i18n.language]}
                                        </ZfTypography>
                                    </ZfCardMediaHeader>
                                </ZfCardMedia>
                            </ZfCard>
                        ))}
                    </ZfStack>
                    <ZfStack direction="column" style={{ flex: 1 }}>
                        {selectedTopic && talkingPointsForSelectedTopic && (
                            <List
                                title={t('titleTalkingPoints')}
                                items={talkingPointsForSelectedTopic.map((talkingPoint) => ({
                                    title: talkingPoint.name[i18n.language],
                                    data: talkingPoint,
                                }))}
                                selectedIndex={selectedTalkingPointIndex}
                                isLoading={!talkingPointsForSelectedTopic || isRefetching}
                                onItemClick={onTalkingPointClick}
                                hasBorderBottom={(index) => index < talkingPointsForSelectedTopic.length - 1}
                            />
                        )}
                    </ZfStack>
                </ZfStack>
            </ZfDialogContent>
            <ZfDialogFooter>
                <ZfTextButton slot="primary" size="medium" disabled={!selectedTalkingPoint} onClick={onDialogConfirm}>
                    {t('eventModalNextButton')}
                </ZfTextButton>
            </ZfDialogFooter>
        </ZfDialog>
    );
};
